// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/siteFields.js

import { canonicalTypeOf } from "./triggerEligibility";

function flattenSample(value, prefix = "") {
  const out = [];
  if (value === null || value === undefined) return out;

  if (Array.isArray(value)) {
    if (prefix) out.push({ key: prefix, value });
    return out;
  }

  if (typeof value === "object") {
    for (const [k, v] of Object.entries(value)) {
      const fullKey = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object" && !Array.isArray(v)) {
        out.push(...flattenSample(v, fullKey));
      } else {
        out.push({ key: fullKey, value: v });
      }
    }
    return out;
  }

  if (prefix) out.push({ key: prefix, value });
  return out;
}

function inferSiteTypeFromValue(value) {
  if (typeof value === "boolean") return "toggle";
  if (typeof value === "number") return "number";
  if (Array.isArray(value)) return "checkbox";
  if (typeof value === "string") {
    const t = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return "date";
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(t)) return "time";
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(t)) return "datetime-local";
    if (/^(true|false)$/i.test(t)) return "toggle";
    if (/^\d+$/.test(t)) return "number";
    return "text";
  }
  return "unknown";
}

export function siteFieldsFromFormNode(formNode) {
  if (!formNode || !Array.isArray(formNode.subQuestions)) return [];

  const out = [];
  for (const child of formNode.subQuestions) {
    if (!child) continue;
    if (child.type && child.type !== "field_type") continue;
    const key = child.field || child.text;
    if (!key) continue;
    out.push({
      key: String(key),
      label: child.text || String(key),
      type: child.fieldType || "text",
      required: !!child.required,
      provenance: "form_field",
      // ⭐ Removed sampleValue: child.placeholder. 
      // Form fields don't have a concrete value at design time.
    });
  }
  return out;
}

export function siteFieldsFromChainNode(parentApiConfig, sampleResponseBody) {
  const out = [];

  const templateStr = parentApiConfig && parentApiConfig.payload_template;
  if (typeof templateStr === "string") {
    const matches = templateStr.match(/\{\{\s*([^}]+?)\s*\}\}/g) || [];
    const seen = new Set();
    for (const m of matches) {
      const key = m.replace(/[\{\}\s]/g, "");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        label: key,
        type: "unknown",
        required: false,
        provenance: "initial_payload",
      });
    }
  }

  let parsed = null;
  if (sampleResponseBody) {
    if (typeof sampleResponseBody === "string") {
      try {
        parsed = JSON.parse(sampleResponseBody);
      } catch {
        parsed = null;
      }
    } else if (typeof sampleResponseBody === "object") {
      parsed = sampleResponseBody;
    }
  }

  if (parsed && typeof parsed === "object") {
    const leaves = flattenSample(parsed, "response_data");
    for (const leaf of leaves) {
      out.push({
        key: leaf.key,
        label: leaf.key,
        type: inferSiteTypeFromValue(leaf.value),
        required: false,
        provenance: "parent_response",
        sampleValue: leaf.value, // ⭐ Used for value-level validation
      });
    }
  }

  return out;
}

export function siteFieldsFromApiConfig(apiConfig) {
  if (!apiConfig) return [];
  const sampleMap = apiConfig.sample_responses_by_status || {};
  const first2xx = Object.keys(sampleMap).find((s) => s.startsWith("2"));
  const sample = first2xx ? sampleMap[first2xx] : null;
  return siteFieldsFromChainNode(apiConfig, sample);
}

export function describeSiteFields(fields) {
  return (fields || []).map(
    (f) => `${f.key} (${canonicalTypeOf(f.type)}, ${f.provenance})`,
  );
}